<script lang="ts">
    import type Blocks from '@/schemas/public/Blocks.ts';
    import type { Field, CulmexJobs, Document, SectionLinkType, AuditLog } from '@/types/types';
    import type { DependencyMap } from '@/types/fieldDependency';
    import FormContainer from '@/components/Form/FormContainer.svelte';
    import { router, useForm } from '@inertiajs/svelte';
    import InlineEditable from '@/components/Inputs/InlineEditable.svelte';
    import FormBlock from '@/components/Form/FormBlock.svelte';
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { CirclePoundSterling, MapPinHouse, Pickaxe, Plus, Trash } from 'lucide-svelte';
    import CreateJobModal from '@/components/jobs/CreateJobModal.svelte';
    import CreateDocumentModal from '@/components/documents/CreateDocumentModal.svelte';
    import { Button } from '@/components/ui/button';
    import RelatedDocuments from '@/components/documents/RelatedDocuments.svelte';
    import Gallery from '@/components/Gallery.svelte';
    import type { Image } from '@/types/types';
    import CustomerTable from '@/components/customers/CustomerTable.svelte';
    import CreateCustomerModal from '@/components/customers/CreateCustomerModal.svelte';
    import LinkExistingModal from '@/components/LinkExistingModal.svelte';
    import LineItems from '@/components/LineItems.svelte';
    import { Link } from 'lucide-svelte';
    import { toast } from 'svelte-sonner';
    import Comments from '@/components/Comments.svelte';
    import { page } from '@inertiajs/svelte';
    import type { Comment } from "@/types/types";
    import { transformToNestedStructure, transformToFlatStructure } from '@/lib/formTransformers';
    import ScaffoldingHandovers from '@/components/jobs/ScaffoldingHandovers.svelte';
    import SiteVisitsList from '@/components/siteVisits/SiteVisitsList.svelte';
    import SiteSurveyForm from '@/components/leads/SiteSurveyForm.svelte';
    import PhasesList from '@/components/phases/PhasesList.svelte';
    import { FileText, Image as ImageIcon, MessageSquare, Star, PersonStanding, History, ArrowUpRight } from 'lucide-svelte';
    import AuditLogComponent from '@/components/AuditLogComponent.svelte';
    import CustomerFeedbacksList from '@/components/jobs/CustomerFeedbacksList.svelte';
    import type { CustomerFeedback } from '@/types/types';
    import VehicleAssignmentTable from '@/components/vehicles/VehicleAssignmentTable.svelte';

    type Props = {
        job: CulmexJobs;
        blocks: (Blocks & { fields: Field[] })[];
        documentBlocks: (Blocks & { fields: Field[] })[];
        customerBlocks: (Blocks & { fields: Field[] })[];
        siteVisitBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyBlocks: (Blocks & { fields: Field[] })[];
        siteSurveyDependencyMap?: DependencyMap;
        siteVisits?: any[];
        relatedDocuments?: Document[];
        csrfToken: string;
        jobImages?: Image[];
        comments: Comment[];
        scaffoldingHandover?: any;
        scaffoldingHandoverBlocks?: (Blocks & { fields: Field[] })[];
        scaffoldingHandoverDependencyMap?: DependencyMap;
        handoverPersonOptions?: { value: string; label: string }[];
        customerTypeOptions?: Record<string, string>;
        customerFeedbacks?: CustomerFeedback[];
        customerFeedbackBlocks?: (Blocks & { fields: Field[] })[];
        entityFieldColumn?: string;
        auditLogs?: AuditLog[];
        jobTypeOptions?: { value: string; label: string }[];
    };

    let {
        job,
        blocks,
        entityFieldColumn,
        documentBlocks,
        customerBlocks,
        siteVisitBlocks,
        siteSurveyBlocks,
        siteSurveyDependencyMap = {},
        siteVisits = [],
        relatedDocuments,
        csrfToken = '',
        jobImages = [],
        comments,
        scaffoldingHandover = null,
        scaffoldingHandoverBlocks = [],
        scaffoldingHandoverDependencyMap = {},
        handoverPersonOptions = [],
        customerTypeOptions = {},
        customerFeedbacks = [],
        customerFeedbackBlocks = [],
        auditLogs = [],
        jobTypeOptions = [],
    }: Props = $props();

    // Local state for images to enable immediate updates
    let localImages = $derived([...jobImages]);

    // Get the entity display name using the entityFieldColumn from the module
    const entityDisplayName = entityFieldColumn && (job as Record<string, any>)[entityFieldColumn]
        ? (job as Record<string, any>)[entityFieldColumn]
        : 'Job Details';

    let breadcrumbs = $state([
        { title: 'Jobs', href: route('jobs.index') },
        { title: `${job.reference ? job.reference + ' · ' : ''}${entityDisplayName}`, href: route('jobs.show', { job: job.id }) }
    ]);

    let deleteForm = $state(useForm({}));

    let actionbuttons = $state([
        ...(job.lead_id && job.lead ? [{
            title: `Source Lead: ${job.lead.reference ? `${job.lead.reference} · ` : ''}${job.lead.name}`,
            href: route('leads.show', { lead: job.lead_id }),
            icon: ArrowUpRight,
        }] : []),
        {
            title: 'Add New Job',
            href: undefined,
            icon: Plus,
            onClick: openCreateJobModal
        },
        {
            title: 'Delete Job',
            actionType: 'delete',
            href: undefined,
            onClick: () => {
                $deleteForm.delete(route('jobs.destroy', { job: job.id }));
            },
            icon: Trash
        }
    ]);

    let showCreateJobModal = $state(false);

    function openCreateJobModal() {
        showCreateJobModal = true;
    }


    let inlineFormInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            inlineFormInner[field.column_name] = field.value;
        }
    }

    // Flatten job data if it has nested structures
    const flattenedJob = transformToFlatStructure(job as any);
    inlineFormInner = { ...inlineFormInner, ...flattenedJob };

    // Fix customers to be just IDs if it's an array of objects
    if (job.customers && Array.isArray(job.customers)) {
        inlineFormInner['customers'] = job.customers.map(c => c.id);
    }

    let inlineForm = $state(useForm(inlineFormInner));
    let editingFieldId: undefined | number = $state(undefined);

    let showCreateDocumentModal = $state(false);

    function openCreateDocumentModal() {
        showCreateDocumentModal = true;
    }

    let showCreateCustomerModal = $state(false);

    function openCreateCustomerModal() {
        showCreateCustomerModal = true;
    }

    let showLinkCustomerModal = $state(false);

    function openLinkCustomerModal() {
        showLinkCustomerModal = true;
    }


    // Section links for quick navigation
    const sectionLinks: SectionLinkType[] = [
        { label: 'Site Visits', id: 'section-site-visits', icon: MapPinHouse },
        { label: 'Line Items', id: 'section-line-items', icon: CirclePoundSterling },
        { label: 'Related Customers', id: 'section-customers', icon: PersonStanding },
        { label: 'Related Job Assignments', id: 'section-assignments', icon: Pickaxe },
        { label: 'Related Documents', id: 'section-documents', icon: FileText },
        { label: 'Gallery', id: 'section-gallery', icon: ImageIcon },
        { label: 'Comments', id: 'section-comments', icon: MessageSquare },
        { label: 'Feedback', id: 'section-feedback', icon: Star },
        { label: 'History', id: 'section-audit-log', icon: History },
    ];

</script>
<!--<Debug {formInner} {form} {$form}/>-->

<svelte:head>
    <title>Job{job.reference ? ` ${job.reference}` : ''} - {entityDisplayName}</title>
</svelte:head>
<AppLayout {breadcrumbs} {actionbuttons} {csrfToken} {sectionLinks}>
    <FormContainer>
        {#snippet renderBlock(block)}
            <FormBlock
                label={block.label}
                collapsible={true}
                defaultOpen={block.label !== 'Site Survey' && block.label !== 'Customer Feedback'}
            >
                {#each block.fields as field (field.id)}
                    <InlineEditable
                        field={field}
                        bind:editingFieldId={editingFieldId}
                        error={$inlineForm.errors[field.column_name]}
                        onSave={(newValue) => {
                                $inlineForm[field.column_name] = newValue;

                                // Transform flat form data to nested structure
                                const transformedData = transformToNestedStructure($inlineForm.data());

                                router.put(route("jobs.update", { job }), transformedData, {
                                    preserveScroll: true,
                                    onSuccess: () => {
                                        editingFieldId = undefined;
                                        router.visit(window.location.href, {
                                            only: ['job', 'relatedDocuments']
                                        });
                                    },
                                    onError: (errors) => {
                                        for (const message of Object.values(errors)) {
                                            toast.error(`Error editing ${field.label}. ${message as string}`);
                                        }
                                    },
                                });
                            }
                        }
                        record={job}
                    />
                {/each}
            </FormBlock>
        {/snippet}

        <!-- General Information -->
        {#if blocks[0]}
            {@render renderBlock(blocks[0])}
        {/if}

        <!-- Site Information + Additional Hire side by side on large screens -->
        {#if blocks[1] || blocks[2]}
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {#if blocks[1]}{@render renderBlock(blocks[1])}{/if}
                {#if blocks[2]}{@render renderBlock(blocks[2])}{/if}
            </div>
        {/if}

        <!-- Any additional blocks beyond index 2 -->
        {#each blocks.slice(3) as block (block.id)}
            {@render renderBlock(block)}
        {/each}

        <!-- Phases (source of truth for the job's dates) -->
        <PhasesList
            recordType="job"
            recordId={job.id}
            phases={(job as any).phases ?? []}
        />

        <VehicleAssignmentTable
            id="section-assignments"
            view="relatedList"
            relatedData={{
                column: "culmex_job_id",
                record_id: job.id,
            }}
            title="Related Job Assignments (appears in app when employee assigned via schedular)"
            {jobTypeOptions}
        />

        <!-- Site Survey Block -->
        <SiteSurveyForm
            record={job}
            recordType="job"
            {siteSurveyBlocks}
            {siteSurveyDependencyMap}
        />

        <!-- Site Visits -->
        <SiteVisitsList
            id="section-site-visits"
            {siteVisits}
            visitableType="App\Models\Job"
            visitableId={job.id}
            blocks={siteVisitBlocks}
        />

        <ScaffoldingHandovers
            jobId={job.id}
            {scaffoldingHandover}
            {scaffoldingHandoverBlocks}
            {scaffoldingHandoverDependencyMap}
            {handoverPersonOptions}
        />


    </FormContainer>

    <LineItems
        id="section-line-items"
        relatedRecordId={job.id}
        relatedRecordType="App\Models\Job"
        lineItems={job.lineItems}
    />

    {#snippet customerTableHeader()}
        <div class="flex gap-2">
            <Button
                onclick={openLinkCustomerModal}
                size="sm"
                variant="outline"
                class="flex items-center gap-x-2">
                <Link class="h-4 w-4" />
                Link Existing
            </Button>
            <Button
                onclick={openCreateCustomerModal}
                size="sm"
                variant="outline"
                class="flex items-center gap-x-2">
                <Plus class="h-4 w-4" />
                Add Customer
            </Button>
        </div>
    {/snippet}

    <CustomerTable
        id="section-customers"
        view="relatedList"
        relatedData={{
                        column : 'related_jobs',
                        record_id : job.id
                    }}
        title="Related Customers"
        headerSnippet={customerTableHeader}
    />


    <CreateCustomerModal
        bind:show={showCreateCustomerModal}
        blocks={customerBlocks}
        {customerTypeOptions}
    />

    <LinkExistingModal
        bind:show={showLinkCustomerModal}
        entityTitle="Customers"
        TableComponent={CustomerTable}
        relatedRecordId={job.id}
        relationshipName="customers"
        syncRoute="jobs.update"
        currentlyRelated={job.customers}
        recordData={inlineFormInner}
    />

    <RelatedDocuments id="section-documents" {relatedDocuments} blocks={documentBlocks} onOpenCreate={openCreateDocumentModal}>
        <CreateDocumentModal
            blocks={documentBlocks}
            bind:show={showCreateDocumentModal}
            relatedRecordType="App\Models\Job"
            relatedRecordId={job.id}
            relatedRecordLabel={job.title}
        />
    </RelatedDocuments>

    <Gallery
        id="section-gallery"
        images={localImages}
        relatedRecordType="App\Models\Job"
        relatedRecordId={job.id}
        csrfToken={csrfToken}
        showSourceBadge={true}
        onUploadComplete={(uploadedImages) => {
            // Update local state with uploaded images
            localImages = [...localImages, ...uploadedImages];
        }}
    />



    <Comments
        id="section-comments"
        commentableId={job.id}
        commentableType="App\Models\Job"
        {comments}
        currentUser={$page.props.auth.user}
    />

    <!-- Customer Feedback -->
    <CustomerFeedbacksList
        id="section-feedback"
        feedbacks={customerFeedbacks}
        blocks={customerFeedbackBlocks}
        jobId={job.id}
        relatedCustomers={job.customers?.map(c => ({ id: c.id, name: c.name, email: c.email }))}
    />

    <AuditLogComponent id="section-audit-log" {auditLogs} />

    <CreateJobModal
        blocks={blocks}
        bind:show={showCreateJobModal}
    />

</AppLayout>

